import React from 'react';
import { Page, UseCase } from '../types';
import { 
    ArrowLeftIcon, OrderTrackingIcon, CartIcon, PropertyIcon, CalendarIcon, 
    HeartIcon, BedIcon, BookIcon, DollarSignIcon, SupportIcon, SalesIcon, CustomIcon, MarketingIcon 
} from './ui/IconComponents';

interface UseCaseDetail {
    title: string;
    heroImage: string;
    challenge: string;
    solution: string;
    features: { icon: React.ReactNode; title: string; description: string }[];
    conversation: { user: string; bot: string }[];
}

const useCaseData: Record<UseCase, UseCaseDetail> = {
    [UseCase.ECommerce]: {
        title: "Revolutionize Your E-commerce Store",
        heroImage: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "High cart abandonment rates, overwhelming customer queries about order status, and the need for 24/7 support are major hurdles for growing e-commerce businesses.",
        solution: "A Wampia AI agent acts as a round-the-clock sales and support assistant directly on WhatsApp. It proactively engages customers, answers questions instantly, and guides them through the purchase process, significantly boosting sales and satisfaction.",
        features: [
            { icon: <OrderTrackingIcon />, title: "Automated Order Tracking", description: "Instantly provide real-time shipping updates and order status to customers." },
            { icon: <CartIcon />, title: "Abandoned Cart Recovery", description: "Automatically send reminders and special offers to customers who left items in their cart." },
            { icon: <SupportIcon />, title: "24/7 Product Support", description: "Answer product questions, provide recommendations, and handle returns anytime." },
        ],
        conversation: [
            { user: "Hi, where is my order #12345?", bot: "Hello! I've checked for you. Order #12345 is currently out for delivery and should arrive today. You can track it live here: [link]" },
            { user: "Thanks! Can you recommend a good laptop for gaming?", bot: "Of course! The 'BladeX Pro' is our top gaming laptop with an RTX 4080 GPU. It's currently on a 10% discount. Would you like me to add it to your cart?" },
        ]
    },
    [UseCase.RealEstate]: {
        title: "Transform Your Real Estate Agency",
        heroImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "Real estate agents are often bogged down with repetitive inquiries about properties, scheduling viewings, and qualifying leads, taking time away from closing deals.",
        solution: "Your AI agent instantly responds to property inquiries on WhatsApp, 24/7. It can show property details, schedule viewings directly into your calendar, and pre-qualify potential buyers, ensuring you only spend time on serious leads.",
        features: [
            { icon: <PropertyIcon />, title: "Instant Property Info", description: "Provide photos, floor plans, and virtual tours of properties on demand." },
            { icon: <CalendarIcon />, title: "Automated Viewing Scheduler", description: "Let clients book viewing times that sync directly with your personal calendar." },
            { icon: <SalesIcon />, title: "Lead Qualification", description: "Ask pre-set questions to determine a lead's budget, timeline, and requirements." },
        ],
        conversation: [
            { user: "I'm interested in the 3-bedroom house on Maple St.", bot: "Great choice! The Maple St. property is 2,100 sq ft and features a newly renovated kitchen. Would you like to see the virtual tour or schedule a viewing?" },
            { user: "Schedule a viewing.", bot: "Certainly. I have openings tomorrow at 2 PM and 4 PM, or Saturday at 11 AM. Which time works best for you?" },
        ]
    },
    [UseCase.Healthcare]: {
        title: "Modernize Your Healthcare Practice",
        heroImage: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "Medical receptionists are swamped with calls for appointments, prescription refills, and basic questions, leading to long wait times and frustrated patients.",
        solution: "The Wampia agent for Healthcare automates routine patient interactions on WhatsApp. It schedules appointments, sends reminders, answers FAQs, and provides pre-visit instructions, freeing up your staff to focus on patient care.",
        features: [
            { icon: <CalendarIcon />, title: "Appointment Management", description: "Allow patients to book, reschedule, or cancel appointments via WhatsApp." },
            { icon: <HeartIcon />, title: "Automated Reminders", description: "Reduce no-shows by sending automatic appointment and medication reminders." },
            { icon: <SupportIcon />, title: "24/7 FAQ Response", description: "Instantly answer common questions about office hours, insurance, and services." },
        ],
        conversation: [
            { user: "I need to book a check-up with Dr. Smith.", bot: "Of course. Dr. Smith's next available appointment is on Thursday at 3:30 PM. Does that work for you?" },
            { user: "Yes, perfect.", bot: "Excellent. I've booked your appointment for Thursday at 3:30 PM. You'll receive a confirmation and a reminder 24 hours before." },
        ]
    },
     [UseCase.Hospitality]: {
        title: "Elevate the Guest Experience in Hospitality",
        heroImage: "https://images.unsplash.com/photo-1542314831-068cd1dbb5eb?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "Hotel staff are constantly fielding requests for room service, information about amenities, and local recommendations, which can be inefficient and inconsistent.",
        solution: "Your AI concierge is available to every guest on WhatsApp, 24/7. It can handle room service orders, book spa appointments, answer questions about the hotel, and act as a local guide, providing a seamless and personalized guest experience.",
        features: [
            { icon: <BedIcon />, title: "In-Room Service Automation", description: "Allow guests to order room service, request amenities, or book services easily." },
            { icon: <CustomIcon />, title: "Personalized Recommendations", description: "Suggest local restaurants, attractions, and events based on guest preferences." },
            { icon: <SupportIcon />, title: "Instant Amenity Information", description: "Provide immediate answers about Wi-Fi, pool hours, and other hotel facilities." },
        ],
        conversation: [
            { user: "What time does the pool close?", bot: "Our pool is open daily from 7 AM to 10 PM. Towels are provided at the poolside. Can I assist with anything else?" },
            { user: "Yes, I'd like to order a club sandwich to room 402.", bot: "Certainly. One club sandwich for room 402. It should arrive in approximately 20-25 minutes. Enjoy!" },
        ]
    },
    [UseCase.Education]: {
        title: "Streamline Communication in Education",
        heroImage: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "Admissions and administrative staff are buried under a mountain of repetitive inquiries about courses, application deadlines, and campus life.",
        solution: "An AI agent for Education serves as a 24/7 admissions and student support assistant. It can answer FAQs, guide students through the application process, provide course details, and send important announcements, improving engagement and efficiency.",
        features: [
            { icon: <SupportIcon />, title: "Admissions & Enrollment FAQ", description: "Instantly answer questions about application status, requirements, and deadlines." },
            { icon: <BookIcon />, title: "Course Information Hub", description: "Provide details on courses, schedules, and prerequisites on demand." },
            { icon: <MarketingIcon />, title: "Automated Announcements", description: "Send campus-wide alerts, event reminders, and important updates to students." },
        ],
        conversation: [
            { user: "What's the application deadline for the MBA program?", bot: "The application deadline for the Fall 2024 MBA program is June 30th. You can find the application form and requirements here: [link]" },
            { user: "Are there any scholarships available?", bot: "Yes, we have several merit-based scholarships. Could you tell me your undergraduate GPA so I can check your eligibility for specific ones?" },
        ]
    },
    [UseCase.FinancialServices]: {
        title: "Secure and Scale Financial Services",
        heroImage: "https://images.unsplash.com/photo-1601597111158-2f954202b3cb?q=80&w=1600&h=900&fit=crop&crop=entropy",
        challenge: "Clients expect quick answers to basic questions, but financial advisors are busy with complex tasks. Capturing and qualifying new leads efficiently is also a constant struggle.",
        solution: "A Wampia agent provides instant, secure responses to common financial queries. It can explain product features, capture and qualify leads for loans or insurance, and schedule consultations, ensuring a professional and timely client experience.",
        features: [
            { icon: <SalesIcon />, title: "24/7 Lead Capture", description: "Engage potential clients on WhatsApp anytime to capture their needs and contact information." },
            { icon: <DollarSignIcon />, title: "Product & Service Explainer", description: "Provide clear, concise information about different loan types, investment products, or insurance policies." },
            { icon: <CalendarIcon />, title: "Advisor Appointment Booking", description: "Seamlessly schedule initial consultations between qualified leads and your financial advisors." },
        ],
        conversation: [
            { user: "I'm interested in a home loan.", bot: "I can certainly help with that. To get started, are you a first-time home buyer?" },
            { user: "Yes.", bot: "Great. We have several loan options for first-time buyers. Would you like to schedule a free, no-obligation call with one of our mortgage advisors to discuss the best fit for you?" },
        ]
    },
};

const ChatBubble: React.FC<{ sender: 'user' | 'bot'; text: string }> = ({ sender, text }) => (
    <div className={`flex ${sender === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-md px-4 py-2 rounded-2xl ${sender === 'user' ? 'bg-[#05F564] text-black rounded-br-none' : 'bg-slate-200 dark:bg-zinc-700 text-slate-800 dark:text-zinc-100 rounded-bl-none'}`}>
            <p className="text-sm">{text}</p>
        </div>
    </div>
);

interface UseCaseDetailPageProps {
    useCase: UseCase;
    setCurrentPage: (page: Page) => void;
}

const UseCaseDetailPage: React.FC<UseCaseDetailPageProps> = ({ useCase, setCurrentPage }) => {
    const data = useCaseData[useCase];

    return (
        <div className="animate-fadeIn">
            {/* Hero */}
            <section className="relative h-64 md:h-80 bg-zinc-900">
                <img src={data.heroImage} alt={data.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative container mx-auto px-4 h-full flex flex-col justify-center text-white z-10">
                    <button onClick={() => setCurrentPage(Page.UseCases)} className="flex items-center gap-2 text-sm text-[#66ffaa] hover:text-[#aaffcc] transition-colors mb-4 w-fit">
                        <ArrowLeftIcon />
                        Back to All Use Cases
                    </button>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">{data.title}</h1>
                </div>
            </section>
            
            <div className="container mx-auto px-4 py-16 md:py-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                    {/* Left Column */}
                    <div className="lg:col-span-7">
                        {/* Challenge & Solution */}
                        <div className="space-y-8">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">The Challenge</h2>
                                <p className="mt-3 text-lg text-slate-600 dark:text-zinc-300">{data.challenge}</p>
                            </div>
                             <div>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">The Wampia Solution</h2>
                                <p className="mt-3 text-lg text-slate-600 dark:text-zinc-300">{data.solution}</p>
                            </div>
                        </div>

                        {/* Key Features */}
                        <div className="mt-12">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Key Features for {useCase}</h2>
                            <div className="space-y-6">
                                {data.features.map(feature => (
                                    <div key={feature.title} className="flex items-start gap-4">
                                        <div className="flex-shrink-0 text-[#05F564] mt-1">{feature.icon}</div>
                                        <div>
                                            <h4 className="font-semibold text-slate-800 dark:text-zinc-100">{feature.title}</h4>
                                            <p className="text-slate-600 dark:text-zinc-300">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-24">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 text-center">Example Conversation</h3>
                            <div className="bg-white dark:bg-black rounded-2xl shadow-lg border border-slate-200 dark:border-zinc-800 p-4">
                                <div className="bg-slate-100/70 dark:bg-[#111111] h-80 rounded-lg p-4 space-y-4 overflow-y-auto flex flex-col">
                                    {data.conversation.map((msg, index) => (
                                       <React.Fragment key={index}>
                                         <ChatBubble sender="user" text={msg.user} />
                                         <ChatBubble sender="bot" text={msg.bot} />
                                       </React.Fragment>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8">
                                <button onClick={() => setCurrentPage(Page.Pricing)} className="w-full bg-[#05F564] text-black font-bold py-3 px-4 rounded-lg shadow-md hover:bg-[#04d458] transition-colors">
                                    Explore Plans
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UseCaseDetailPage;
